import { WsMessageEvent, WsClientSocket } from "./utils/wsModule";
import websocketModifier, {
	ModifiedWebSocket,
} from "./utils/websocketModifier.util";
import WeakFunctionPool from "./functionResolvers/weakFunctionPool.functionResolver";
import DefaultResolver from "./modelResolvers/default.modelResolver";
import { PRPC_OW_VERSION } from "./version";
import typeAssert from "./utils/typeAssert.util";
import RuntimeError from "./utils/error.utils";
import UniversalRPC from "./universalRPC";
import badRequestUtil from "./utils/badRequest.util";
import NoCompressionResolver from "./compressResolvers/noCompression.compressionResolver";
import UuidIdResolver from "./idResolvers/uuid.idResolver";
import satisfies from "./utils/version.util";
import { TransferFlags, normalizeTransferFlags } from "./transfer";
import {
	appendConnectionKeyToUrl,
	connectionKeyProtocol,
} from "./connectionKey";

function handshakeText(data: unknown): string | null {
	if (typeof data === "string") {
		return data;
	}
	if (data instanceof ArrayBuffer) {
		const bytes = new Uint8Array(data);
		if (bytes.length === 0) {
			return null;
		}
		if (bytes[0] === 1 || bytes[0] === 2) {
			return null;
		}
		return new TextDecoder().decode(bytes);
	}
	if (ArrayBuffer.isView(data)) {
		const bytes = new Uint8Array(
			data.buffer,
			data.byteOffset,
			data.byteLength,
		);
		if (bytes.length === 0) {
			return null;
		}
		if (bytes[0] === 1 || bytes[0] === 2) {
			return null;
		}
		return new TextDecoder().decode(bytes);
	}
	return null;
}

interface WebsocketProto {
	new (address: string | URL, protocols?: string | string[]): WsClientSocket;
}

export type ClientOptions = {
	callback?: Function;
	transfer?: TransferFlags;
	connectionKey?: string;
};

export class Client {
	private readonly WebsocketImplementation: WebsocketProto;

	private callback: Function;

	websocket: ModifiedWebSocket;

	public key = "";

	private options: ClientOptions & { callback: Function; version: string };

	constructor(
		WebsocketImplementation: WebsocketProto,
		address: string | URL,
		protocols: string | string[],
		options: ClientOptions,
		callback: Function = () => {},
	) {
		const finalOptions = options || {};
		const { connectionKey } = finalOptions;
		this.options = {
			...finalOptions,
			callback: finalOptions.callback || callback,
			version: PRPC_OW_VERSION,
			connectionKey,
		};
		this.WebsocketImplementation = WebsocketImplementation;
		this.callback = this.options.callback;

		const protocolList = [
			...(Array.isArray(protocols) ? protocols : [protocols]),
			"prpc",
		].filter(Boolean);

		let connectUrl: string | URL = address;
		if (connectionKey) {
			protocolList.push(connectionKeyProtocol(connectionKey));
			connectUrl = appendConnectionKeyToUrl(address, connectionKey);
		}

		this.websocket = websocketModifier(
			new this.WebsocketImplementation(connectUrl, protocolList),
		);
		if ("binaryType" in this.websocket) {
			(
				this.websocket as ModifiedWebSocket & {
					binaryType?: string;
				}
			).binaryType = "arraybuffer";
		}
	}

	public init = () =>
		new Promise((resolve, reject) => {
			let settled = false;
			let negotiatedTransfer = normalizeTransferFlags(
				this.options.transfer,
			);

			let handshakeTimer: ReturnType<typeof setTimeout> | undefined;

			const finish = (error: Error | null, session?: unknown) => {
				if (settled) return;
				settled = true;
				if (handshakeTimer) clearTimeout(handshakeTimer);
				this.websocket.removeEventListener("close", onHandshakeClose);
				this.websocket.removeEventListener("error", onHandshakeError);
				this.websocket.removeEventListener(
					"message",
					initMessageOperator,
				);

				if (error) {
					this.callback(error, null);
					reject(error);
					return;
				}
				this.callback(null, session);
				resolve(session);
			};

			const onHandshakeClose = () => {
				finish(new Error("WebSocket closed during handshake"));
			};
			const onHandshakeError = () => {
				finish(new Error("WebSocket error during handshake"));
			};
			handshakeTimer = setTimeout(() => {
				finish(new Error("prpcow handshake timeout"));
				try {
					this.websocket.close();
				} catch {
					// ignore
				}
			}, 20_000);

			const initMessageOperator = (event: WsMessageEvent) => {
				try {
					const text = handshakeText(event.data);
					if (text === null) {
						return;
					}
					const clientRequest = JSON.parse(text);
					typeAssert(
						clientRequest,
						{
							init: () => {
								this.key = clientRequest.data.key;
								if (clientRequest.data.transfer) {
									negotiatedTransfer = normalizeTransferFlags(
										clientRequest.data.transfer,
									);
								}

								if (
									!(
										satisfies(
											this.options.version,
											clientRequest.data.version,
										) &&
										WeakFunctionPool.isCompatibleWith(
											clientRequest.data.functionResolver,
										) &&
										DefaultResolver.isCompatibleWith(
											clientRequest.data.modelResolver,
										) &&
										NoCompressionResolver.isCompatibleWith(
											clientRequest.data.compressResolver,
										) &&
										UuidIdResolver.isCompatibleWith(
											clientRequest.data.idResolver,
										)
									)
								) {
									throw new RuntimeError(
										"server version or resolver is not compatible with client version",
										400,
										"Bad Request",
									);
								}

								this.websocket.send(
									JSON.stringify({
										type: "init",
										data: {
											key: this.key,
											version: this.options.version,
											functionResolver:
												WeakFunctionPool.typeName(),
											modelResolver:
												DefaultResolver.typeName(),
											compressResolver:
												NoCompressionResolver.typeName(),
											idResolver:
												UuidIdResolver.typeName(),
										},
									}),
								);
							},
							ready: () => {
								this.websocket.sessionId = this.key;

								const session = new UniversalRPC(
									this.websocket,
									{ transfer: negotiatedTransfer },
								);

								finish(null, session);
							},
						},
						() => {
							throw new RuntimeError(
								`Could not understand request. "${
									clientRequest.type
										? `Received type: ${clientRequest.type}`
										: "No type received"
								}"`,
								400,
								"Bad request",
							);
						},
					);
				} catch (e: any) {
					try {
						this.websocket.send(JSON.stringify(badRequestUtil(e)));
					} catch {
						// ignore
					}
					try {
						this.websocket.close();
					} catch {
						// ignore
					}
					finish(e);
				}
			};

			this.websocket.addEventListener("message", initMessageOperator);
			this.websocket.addEventListener("close", onHandshakeClose);
			this.websocket.addEventListener("error", onHandshakeError);
		});
}
